import { faDownload, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { StatementTransactionModel } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalStatementService from '../services/portalStatementService';
import { useLoginModalStore } from '../stores/loginModalStore';

const TRANSACTION_TYPE_LABELS: Record<
	StatementTransactionModel['type'],
	string
> = {
	INVOICE: 'Invoice',
	PAYMENT: 'Payment',
	CREDIT: 'Credit Memo',
	ADJUSTMENT: 'Adjustment',
};

/**
 * Statement detail (PD-14046) — renders the stored snapshot with a
 * portal-native, mobile-first layout (transaction cards instead of the CRM's
 * wide tables) and a Download button that streams the snapshot-rendered PDF.
 */
export function StatementDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const [isDownloading, setIsDownloading] = useState(false);

	const statementId = id != null ? parseInt(id, 10) : NaN;

	const { data, isLoading } = useQuery({
		queryKey: ['portal-statement', statementId],
		queryFn: () =>
			portalStatementService.getStatementDetail(token ?? '', statementId),
		enabled: token != null && !Number.isNaN(statementId),
		staleTime: 60_000,
	});

	const record = data?.data ?? null;
	const snapshot = record?.snapshotJson ?? null;

	const handleDownload = useCallback(async () => {
		if (record == null || token == null) {
			return;
		}
		setIsDownloading(true);
		try {
			const blob = await portalStatementService.downloadStatementPdf(
				token,
				record.id,
			);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${record.statementNumber}.pdf`;
			a.click();
			window.URL.revokeObjectURL(url);
		} catch (err) {
			console.error('Error downloading statement PDF:', err);
		} finally {
			setIsDownloading(false);
		}
	}, [record, token]);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-3xl mx-auto">
			<MobilePageHeader
				title={record?.statementNumber ?? 'Statement'}
				backPath="/statements"
			/>
			<div className="flex items-center justify-between mb-4">
				<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
					{record != null ? t(record.statementNumber) : t('Statement')}
				</h1>
				{record != null && (
					<button
						type="button"
						onClick={() => {
							void handleDownload();
						}}
						disabled={isDownloading}
						className="ml-auto px-4 py-2 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
					>
						<FontAwesomeIcon icon={faDownload} className="mr-2" />
						{isDownloading ? t('Preparing...') : t('Download PDF')}
					</button>
				)}
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-16 text-brand-muted">
					<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
					{t('Loading...')}
				</div>
			) : snapshot == null ? (
				<div className="flex items-center justify-center py-16 text-brand-muted">
					{t('Statement not found')}
				</div>
			) : (
				<div className="flex flex-col gap-4">
					{/* Parties */}
					<div className="grid grid-cols-2 gap-3">
						<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
							<div className="text-xs uppercase tracking-wide text-brand-muted mb-1">
								{t('Remit To')}
							</div>
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{t(snapshot.remitTo.name)}
							</div>
							{snapshot.remitTo.addressLines.map((line, i) => (
								<div key={i} className="text-xs text-brand-muted">
									{t(line)}
								</div>
							))}
						</div>
						<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
							<div className="text-xs uppercase tracking-wide text-brand-muted mb-1">
								{t('Bill To')}
							</div>
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{t(snapshot.billTo.name)}
							</div>
							{snapshot.billTo.addressLines.map((line, i) => (
								<div key={i} className="text-xs text-brand-muted">
									{t(line)}
								</div>
							))}
						</div>
					</div>

					{/* Meta */}
					<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
						{[
							{
								label: 'Statement Date',
								value: formatStatementDate(snapshot.statementDate),
							},
							{ label: 'Payment Terms', value: snapshot.paymentTerms ?? '-' },
							{
								label: 'Total Balance Due',
								value: formatCurrency(snapshot.totalBalance) ?? '-',
							},
						].map((meta) => (
							<div
								key={meta.label}
								className="bg-brand-surface border border-brand-border rounded-xl p-3"
							>
								<div className="text-xs text-brand-muted">{t(meta.label)}</div>
								<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
									{t(meta.value)}
								</div>
							</div>
						))}
					</div>

					{/* Monthly activity — mobile-first transaction cards */}
					{snapshot.months.length === 0 ? (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-4 text-sm text-brand-muted">
							{t('No activity in the statement period.')}
						</div>
					) : (
						snapshot.months.map((month) => (
							<div key={month.key}>
								<div className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
									{t(month.label)}
								</div>
								<div className="flex flex-col gap-2">
									{month.transactions.map((txn, index) => {
										const isCharge =
											txn.type === 'INVOICE' || txn.type === 'CREDIT';
										return (
											<div
												key={`${month.key}-${index}`}
												className="bg-brand-surface border border-brand-border rounded-xl p-3 flex items-start justify-between gap-3"
											>
												<div className="min-w-0">
													<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
														{t(TRANSACTION_TYPE_LABELS[txn.type])}{' '}
														{t(txn.number !== '' ? txn.number : '')}
													</div>
													<div className="text-xs text-brand-muted">
														{formatStatementDate(txn.date)}
														{txn.dueDate != null && txn.dueDate !== '' && (
															<>
																{' · '}
																{t('Due')} {formatStatementDate(txn.dueDate)}
															</>
														)}
													</div>
												</div>
												<div className="text-right shrink-0">
													<div
														className={`text-sm font-semibold ${
															isCharge
																? 'text-gray-900 dark:text-gray-100'
																: 'text-green-700 dark:text-green-300'
														}`}
													>
														{isCharge
															? formatCurrency(txn.charge)
															: `-${formatCurrency(txn.payment) ?? ''}`}
													</div>
													<div className="text-xs text-brand-muted">
														{t('Balance')} {formatCurrency(txn.balance)}
													</div>
												</div>
											</div>
										);
									})}
									<div className="flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
										<span>
											{t(month.label)} {t('ending balance')}
										</span>
										<span>{formatCurrency(month.endingBalance)}</span>
									</div>
								</div>
							</div>
						))
					)}

					{/* Aging summary */}
					<div>
						<div className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
							{t('Aging Summary')}
						</div>
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
							{[
								{ label: 'Current', value: snapshot.aging.current },
								{ label: '1 - 30 Days', value: snapshot.aging.days1to30 },
								{ label: '31 - 60 Days', value: snapshot.aging.days31to60 },
								{ label: '61 - 90 Days', value: snapshot.aging.days61to90 },
								{ label: 'Over 90 Days', value: snapshot.aging.days90plus },
								{ label: 'Total Due', value: snapshot.aging.total },
							].map((cell) => (
								<div
									key={cell.label}
									className="bg-brand-surface border border-brand-border rounded-xl p-3"
								>
									<div className="text-xs text-brand-muted">
										{t(cell.label)}
									</div>
									<div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
										{formatCurrency(cell.value)}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
